import { Component, Input, Output, ViewChild, Inject, Optional, OnDestroy, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AutosaveControlService, AutosavePage } from '@service/auto-save-controller.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-confirmation-window',
    templateUrl: './confirmation-window.component.html',
    styleUrls: ['./confirmation-window.component.css'],
    standalone: false,
    providers: [
        { provide: MatDialogRef, useValue: null },
        { provide: MAT_DIALOG_DATA, useValue: null },
    ],
})
export class ConfirmationWindowComponent implements OnDestroy {
    @ViewChild('overlay') overlay: any;
    public onOptionSelected: Subject<ConfirmationWindowTypes>;

    @Input() mainLabel: string = '';
    @Input() subLabel: string = '';
    @Input() discardLabel: string = '';
    @Input() goBackLabel: string = '';
    @Input() saveLabel: string = '';
    @Input() title: string = '';
    @Input() thirdLabel: string = '';

    @Input() inverseColors: boolean = false;
    @Input() smallDialog: boolean = false;
    @Input() allButtonsLeft: boolean = false;
    @Input() saveIsDisabled: boolean = false;
    @Input() comment: string = null;
    @Input() maxLength: number = 50;
    @Input() variant: string = '';
    @Input() errorIcon: boolean = false;

    @Output() commentChange = new EventEmitter<string>();

    style = { display: 'none' };
    stopAutoSave: boolean = false;
    autoSavePage: AutosavePage;

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
        @Optional() private dialogRef: MatDialogRef<ConfirmationWindowComponent>,
        private autosaveControlService: AutosaveControlService
    ) {
        this.onOptionSelected = new Subject();

        if (this.dialogData) {
            this.title = this.dialogData.title ?? this.title;
            this.mainLabel = this.dialogData.message ?? this.mainLabel;
            this.subLabel = this.dialogData.subLabel ?? this.subLabel;

            this.discardLabel = this.dialogData.discardButton ?? this.discardLabel;
            this.saveLabel = this.dialogData.saveButton ?? this.saveLabel;
            this.goBackLabel = this.dialogData.backButton ?? this.goBackLabel;

            this.inverseColors = this.dialogData.inverseColors ?? this.inverseColors;
            this.smallDialog = this.dialogData.smallDialog ?? this.smallDialog;
            this.allButtonsLeft = this.dialogData.allButtonsLeft ?? this.allButtonsLeft;
            this.saveIsDisabled = this.dialogData.saveIsDisabled ?? this.saveIsDisabled;
            this.comment = this.dialogData.comment ?? this.comment;
        }
    }

    ngOnDestroy(): void {
        this.onOptionSelected.complete();
    }

    public setVisible(visible: boolean, stopAutosave = false, autoSavePage?: AutosavePage) {
        this.style.display = visible ? 'block' : 'none';

        if (stopAutosave && autoSavePage) {
            this.autosaveControlService.setDialogOpen(autoSavePage, visible);
        }

        this.stopAutoSave = visible ? stopAutosave : false;
        this.autoSavePage = visible ? autoSavePage : undefined;
    }

    discardChanges() {
        this.emitValue(ConfirmationWindowTypes.DISCARD);
    }

    goBack() {
        this.emitValue(ConfirmationWindowTypes.GO_BACK);
    }

    saveChanges() {
        if (this.saveIsDisabled) return;
        this.emitValue(ConfirmationWindowTypes.SAVE);
    }

    thirdButtonClick() {
        this.emitValue(ConfirmationWindowTypes.DELETE_FOLDER);
    }

    onCommentUpdated(newValue: string) {
        this.comment = newValue;
        this.commentChange.emit(newValue);
    }

    private emitValue(value: ConfirmationWindowTypes) {
        this.onOptionSelected.next(value);
        if (this.dialogRef) {
            this.dialogRef.close(value);
        } else {
            this.setVisible(false, this.stopAutoSave, this.autoSavePage);
        }
    }
}

export enum ConfirmationWindowTypes {
    UNDEFINED = 'undefined',
    DISCARD = 'discard',
    GO_BACK = 'goBack',
    SAVE = 'save',
    DELETE_FOLDER = 'deleteFolder',
}
